// v10/src/components/admin/lifespan/GlobalAttentionTable.js
//
// Tabla "Componentes que requieren atención" del módulo Vida límite (A6.1) — Tarea 2.
// Reemplaza el placeholder de GlobalView.
//
// Filtros:
//   • status   → CSV server-side (backend soporta `status=critical,expired,next`)
//   • aircraft → multi-select client-side (backend solo acepta single aircraft_id)
//   • category → multi-select client-side (backend solo acepta single category)
//
// La decisión de filtrar aircraft/category client-side es porque global-attention
// devuelve un dataset acotado por definición (solo next/critical/expired), y evita
// tocar el backend (fuera de scope T2). Confirmada con David.
//
// Polling: cada POLLING_INTERVALS.maintenance, pausable con isActive.
// Click en una fila → callback onSelect({ aircraft_id, component_id }) que sube
// a LifespanTab para coordinar la navegación.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle, Loader, RefreshCw, Filter, X as XIcon, ChevronDown, ShieldCheck,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Cog, Fan, Zap, Battery, Flame, Shield, Wind, Sparkle, Droplet, Wrench, Eye,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/apiService';
import componentLifespanService from '../../../services/componentLifespanService';
import { POLLING_INTERVALS } from '../../../config/realtime';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const STATUS_OPTIONS = ['next', 'critical', 'expired'];
const STATUS_FALLBACK_COLOR = {
  next:     '#eab308',
  critical: '#ea580c',
  expired:  '#dc2626',
};

const CATEGORY_KEYS = [
  'engine', 'propeller', 'magneto', 'elt_battery', 'fire_extinguisher',
  'seatbelt', 'pitot_tube', 'spark_plug', 'oil_filter', 'other',
];

const CATEGORY_ICONS = {
  engine:            Cog,
  propeller:         Fan,
  magneto:           Zap,
  elt_battery:       Battery,
  fire_extinguisher: Flame,
  seatbelt:          Shield,
  pitot_tube:        Wind,
  spark_plug:        Sparkle,
  oil_filter:        Droplet,
  other:             Wrench,
};

const hexToRgba = (hex, alpha = 0.14) => {
  if (!hex || !hex.startsWith('#')) return `rgba(99,102,241,${alpha})`;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return `rgba(99,102,241,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const GlobalAttentionTable = ({
  darkMode,
  isActive = true,
  locale = 'es-AR',
  onSelect,
}) => {
  const { t } = useTranslation('admin');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aircraftList, setAircraftList] = useState([]);

  // Filtros: arrays vacíos = "todos"
  const [aircraftFilter, setAircraftFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([...STATUS_OPTIONS]); // por defecto los 3
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const pollRef = useRef(null);
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  // ── Carga de aeronaves activas (una vez) ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/aircraft', { params: { is_active: 'true' } });
        if (!cancelled) setAircraftList(res?.data?.data || []);
      } catch (_) {
        if (!cancelled) setAircraftList([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Carga de componentes con atención ─────────────────────────────
  const load = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    setError('');
    const params = {};
    if (statusFilter.length > 0 && statusFilter.length < STATUS_OPTIONS.length) {
      params.status = statusFilter.join(',');
    }
    try {
      const res = await componentLifespanService.getGlobalAttention(params);
      if (!isMounted.current) return;
      setItems(res?.data?.data || []);
    } catch (_) {
      if (!isMounted.current) return;
      setError(t('lifespan_global_table_load_error'));
    } finally {
      if (isMounted.current && showLoader) setLoading(false);
    }
  }, [statusFilter, t]);

  // Carga inicial / cuando cambia el filtro de status
  useEffect(() => {
    load(true);
  }, [load]);

  // Polling
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!isActive) return undefined;
    pollRef.current = setInterval(() => load(false), POLLING_INTERVALS.maintenance);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isActive, load]);

  // ── Filtros client-side de aircraft + category ────────────────────
  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (aircraftFilter.length > 0 && !aircraftFilter.includes(it.aircraft_id)) return false;
      if (categoryFilter.length > 0 && !categoryFilter.includes(it.category)) return false;
      return true;
    });
  }, [items, aircraftFilter, categoryFilter]);

  // Paginación
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePage = Math.min(page, totalPages);
  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [safePage, page]);

  const pagedItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, safePage, pageSize]);

  const hasFilters = aircraftFilter.length > 0
    || categoryFilter.length > 0
    || statusFilter.length !== STATUS_OPTIONS.length;

  const clearFilters = () => {
    setAircraftFilter([]);
    setCategoryFilter([]);
    setStatusFilter([...STATUS_OPTIONS]);
    setPage(1);
  };

  // ── Estilos ───────────────────────────────────────────────────────
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const cardBg    = darkMode ? 'rgba(255,255,255,0.03)' : 'white';
  const cardBorder = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';

  const headerThStyle = {
    padding: '10px 12px', textAlign: 'left',
    fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: fgFaint,
    borderBottom: cardBorder,
    whiteSpace: 'nowrap',
  };
  const tdStyle = {
    padding: '12px', verticalAlign: 'middle',
    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f1f5f9',
    fontSize: 13, color: fgPrimary,
  };

  // ── Helpers de "vencimiento" para la columna de la tabla ──────────
  const getExpirationText = (it) => {
    const hasHours = it.hours_remaining != null;
    const hasDate  = it.days_remaining != null;
    const hoursRem = hasHours ? Number(it.hours_remaining) : null;
    const daysRem  = hasDate ? Number(it.days_remaining) : null;

    // Si tiene ambos, mostrar el peor (el que está más vencido o menos remanente).
    // Aproximación razonable: convertir días remanentes a horas/día estimadas no es buena;
    // mostramos ambos si tiene los dos.
    const parts = [];
    if (hasHours) {
      if (hoursRem <= 0) {
        parts.push(t('lifespan_global_table_hours_exceeded', {
          count: Math.abs(Math.round(hoursRem)),
        }));
      } else {
        parts.push(t('lifespan_global_table_expires_in_hours', {
          count: Math.round(hoursRem),
        }));
      }
    }
    if (hasDate) {
      if (daysRem <= 0) {
        parts.push(t('lifespan_global_table_days_overdue', {
          count: Math.abs(daysRem),
        }));
      } else {
        parts.push(t('lifespan_global_table_expires_in_days', {
          count: daysRem,
        }));
      }
    }
    return parts.join(' · ') || '—';
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        background: cardBg, border: cardBorder, borderRadius: 20,
        padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 36, height: 36, borderRadius: 12,
              background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AlertCircle style={{ width: 18, height: 18, color: '#6366f1' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary, letterSpacing: '-0.2px' }}>
              {t('lifespan_global_table_title')}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: fgMuted }}>
              {t('lifespan_global_table_subtitle', { count: filteredItems.length })}
            </p>
          </div>
        </div>
        <motion.button
          type="button"
          onClick={() => load(true)}
          whileTap={{ scale: 0.96 }}
          aria-label={t('lifespan_global_table_refresh')}
          title={t('lifespan_global_table_refresh')}
          style={{
            padding: 8, borderRadius: 10, border: 'none', cursor: 'pointer',
            background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
            color: fgMuted, display: 'flex',
          }}
        >
          <RefreshCw style={{ width: 14, height: 14 }} />
        </motion.button>
      </div>

      {/* Filtros */}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
          padding: '10px 0', borderTop: cardBorder, borderBottom: cardBorder,
        }}
      >
        <span
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, fontWeight: 700, color: fgFaint, textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <Filter style={{ width: 12, height: 12 }} />
          {t('fuel_movements_table_filters', { defaultValue: 'Filtros' })}
        </span>

        <MultiSelect
          label={t('lifespan_global_table_filter_aircraft')}
          allLabel={t('lifespan_global_table_filter_aircraft_all')}
          options={aircraftList.map((a) => ({
            value: a.id,
            label: `${a.registration || ''}${(a.manufacturer || a.model_name) ? ` · ${a.manufacturer || ''} ${a.model_name || ''}`.trim() : ''}`,
          }))}
          selected={aircraftFilter}
          onChange={(next) => { setAircraftFilter(next); setPage(1); }}
          darkMode={darkMode}
          t={t}
        />

        <MultiSelect
          label={t('lifespan_global_table_filter_category')}
          allLabel={t('lifespan_global_table_filter_category_all')}
          options={CATEGORY_KEYS.map((k) => ({
            value: k,
            label: t(`component_category_${k}`),
          }))}
          selected={categoryFilter}
          onChange={(next) => { setCategoryFilter(next); setPage(1); }}
          darkMode={darkMode}
          t={t}
        />

        <MultiSelect
          label={t('lifespan_global_table_filter_status')}
          allLabel={t('lifespan_global_table_filter_status_all')}
          options={STATUS_OPTIONS.map((s) => ({
            value: s,
            label: t(`component_status_${s}`),
          }))}
          selected={statusFilter}
          onChange={(next) => { setStatusFilter(next); setPage(1); }}
          darkMode={darkMode}
          t={t}
        />

        {hasFilters && (
          <button
            type="button" onClick={clearFilters}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 10px', borderRadius: 10, border: 'none',
              background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
              color: fgMuted, cursor: 'pointer',
              fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
            }}
          >
            <XIcon style={{ width: 12, height: 12 }} />
            {t('lifespan_global_table_clear_filters')}
          </button>
        )}
      </div>

      {/* Loading / error / empty / table */}
      {loading && items.length === 0 && !error ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '32px 0', color: fgMuted, fontSize: 13,
          }}
        >
          <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
          <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
          {t('lifespan_global_table_loading')}
        </div>
      ) : error ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '12px 16px', borderRadius: 14,
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.25)',
            color: '#dc2626', fontSize: 13, fontWeight: 600,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
            {error}
          </span>
          <button
            type="button"
            onClick={() => load(true)}
            style={{
              padding: '6px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: '#dc2626', color: 'white',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
            }}
          >
            {t('lifespan_global_table_retry')}
          </button>
        </div>
      ) : filteredItems.length === 0 ? (
        <EmptyState
          filtered={hasFilters && items.length > 0}
          onClearFilters={clearFilters}
          darkMode={darkMode}
          t={t}
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%', borderCollapse: 'collapse',
              fontFamily: 'inherit',
            }}
          >
            <thead>
              <tr>
                <th style={headerThStyle}>{t('lifespan_global_table_col_status')}</th>
                <th style={headerThStyle}>{t('lifespan_global_table_col_aircraft')}</th>
                <th style={headerThStyle}>{t('lifespan_global_table_col_component')}</th>
                <th style={headerThStyle}>{t('lifespan_global_table_col_expiration')}</th>
                <th style={{ ...headerThStyle, textAlign: 'right' }}>{t('lifespan_global_table_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((it) => {
                const status = it.status || 'next';
                const color = it.status_color || STATUS_FALLBACK_COLOR[status] || '#64748b';
                const Icon = CATEGORY_ICONS[it.category] || Wrench;
                const aircraftLabel = it.aircraft_registration
                  ? (it.aircraft_model
                      ? `${it.aircraft_registration} · ${it.aircraft_model}`
                      : it.aircraft_registration)
                  : '—';
                return (
                  <tr
                    key={it.id}
                    onClick={() => onSelect?.({ aircraftId: it.aircraft_id, componentId: it.id })}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '3px 10px', borderRadius: 999,
                          background: hexToRgba(color, 0.14), color,
                          fontSize: 11, fontWeight: 800,
                          textTransform: 'uppercase', letterSpacing: '0.4px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t(`component_status_${status}`)}
                      </span>
                    </td>
                    <td style={tdStyle}>{aircraftLabel}</td>
                    <td style={tdStyle}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Icon style={{ width: 14, height: 14, color }} />
                        <span style={{ fontWeight: 700 }}>{it.name}</span>
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: fgMuted }}>{getExpirationText(it)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect?.({ aircraftId: it.aircraft_id, componentId: it.id });
                        }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '6px 12px', borderRadius: 10, border: 'none',
                          background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                          color: fgPrimary, cursor: 'pointer',
                          fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                        }}
                      >
                        <Eye style={{ width: 13, height: 13 }} />
                        {t('lifespan_global_table_action_view')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      {filteredItems.length > pageSize && (
        <div
          style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
            justifyContent: 'space-between', paddingTop: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: fgMuted }}>
            <span>{t('lifespan_global_table_page_size')}</span>
            <select
              value={pageSize}
              onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
              style={{
                padding: '6px 8px', borderRadius: 10,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'white',
                color: fgPrimary, fontSize: 12, fontFamily: 'inherit',
                cursor: 'pointer', outline: 'none',
              }}
            >
              {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              type="button" onClick={() => setPage(1)} disabled={safePage === 1}
              aria-label="first" style={{
                padding: 6, borderRadius: 8, border: 'none',
                background: 'transparent', color: safePage === 1 ? fgFaint : fgMuted,
                cursor: safePage === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronsLeft style={{ width: 14, height: 14 }} />
            </button>
            <button
              type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}
              aria-label="previous" style={{
                padding: 6, borderRadius: 8, border: 'none',
                background: 'transparent', color: safePage === 1 ? fgFaint : fgMuted,
                cursor: safePage === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronLeft style={{ width: 14, height: 14 }} />
            </button>
            <span style={{ fontSize: 12, color: fgMuted, padding: '0 6px' }}>
              {t('lifespan_global_table_page_of', { current: safePage, total: totalPages })}
            </span>
            <button
              type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
              aria-label="next" style={{
                padding: 6, borderRadius: 8, border: 'none',
                background: 'transparent', color: safePage >= totalPages ? fgFaint : fgMuted,
                cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronRight style={{ width: 14, height: 14 }} />
            </button>
            <button
              type="button" onClick={() => setPage(totalPages)} disabled={safePage >= totalPages}
              aria-label="last" style={{
                padding: 6, borderRadius: 8, border: 'none',
                background: 'transparent', color: safePage >= totalPages ? fgFaint : fgMuted,
                cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronsRight style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Subcomponentes ──────────────────────────────────────────────────

const MultiSelect = ({ label, allLabel, options, selected, onChange, darkMode, t: _t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  const buttonText = selected.length === 0
    ? `${label}: ${allLabel}`
    : `${label}: ${selected.length}`;

  const toggleValue = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 12px', borderRadius: 10,
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          background: darkMode ? 'rgba(255,255,255,0.05)' : 'white',
          color: fgPrimary, cursor: 'pointer',
          fontSize: 12.5, fontFamily: 'inherit',
          whiteSpace: 'nowrap',
        }}
      >
        {buttonText}
        <ChevronDown style={{ width: 12, height: 12, color: fgFaint }} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0,
              minWidth: 240, maxHeight: 280, overflowY: 'auto',
              background: darkMode ? '#1f2937' : 'white',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
              borderRadius: 12, padding: 6,
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              zIndex: 30,
            }}
          >
            {options.length === 0 ? (
              <p style={{ margin: 0, padding: 10, fontSize: 12, color: fgMuted }}>
                {allLabel}
              </p>
            ) : options.map((opt) => {
              const isSel = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                    fontSize: 12.5, color: fgPrimary,
                    background: isSel
                      ? (darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)')
                      : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleValue(opt.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const EmptyState = ({ filtered, onClearFilters, darkMode, t }) => {
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 14, padding: '48px 24px',
        borderRadius: 18,
        border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
        background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 56, height: 56, borderRadius: 18,
          background: filtered
            ? (darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)')
            : (darkMode ? 'rgba(22,163,74,0.18)' : 'rgba(22,163,74,0.10)'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <ShieldCheck
          style={{
            width: 26, height: 26,
            color: filtered ? '#6366f1' : '#16a34a',
          }}
        />
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary }}>
          {filtered ? t('lifespan_global_empty_filtered_title') : t('lifespan_global_empty_title')}
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: fgMuted, maxWidth: 460 }}>
          {filtered ? t('lifespan_global_empty_filtered_desc') : t('lifespan_global_empty_desc')}
        </p>
      </div>
      {filtered && (
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={onClearFilters}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 16px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
            color: 'white', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
          }}
        >
          <XIcon style={{ width: 14, height: 14 }} />
          {t('lifespan_global_table_clear_filters')}
        </motion.button>
      )}
    </div>
  );
};

export default GlobalAttentionTable;
